import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Download, Trash2, FolderOpen, Search, X, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { downloadDocument, viewDocument } from '../../services/downloadService';
import { getToken } from '../../services/tokenService';

export default function DocumentsGED({ getAuthHeader: getAuthHeader }) {
  const { t } = useTranslation();
  // Ce composant est utilisé dans les dashboards pour gérer la GED
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [_categoriesLoaded, setCategoriesLoaded] = useState(false);

  // Form state pour upload
  const [uploadForm, setUploadForm] = useState({
    file: null,
    categorie: '',
    description: '',
    tags: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadCategories();
    loadDocuments();
    loadStats();
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [selectedCategory, searchQuery]);

  const getAuthHeaders = () => {
    const token = getToken();
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  const loadCategories = async () => {
    try {
      // Utiliser la route accessible à tous les utilisateurs
      const response = await fetch(`${API_URL}/api/ged/categories`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
        setCategoriesLoaded(true);
      } else {
        // Fallback: utiliser des catégories par défaut
        setCategories([
          { value: "devis", label: "Devis" },
          { value: "factures", label: "Factures" },
          { value: "photos", label: "Photos" },
          { value: "courrier", label: "Courrier" },
          { value: "rapport", label: "Rapport" },
          { value: "piece_identite", label: "Pièce d'identité" },
          { value: "contrat", label: "Contrat" },
          { value: "attestation", label: "Attestation" },
          { value: "autre_document", label: "Autre document" }
        ]);
        setCategoriesLoaded(true);
      }
    } catch {
      // Fallback en cas d'erreur
      setCategories([
        { value: "devis", label: "Devis" },
        { value: "factures", label: "Factures" },
        { value: "photos", label: "Photos" },
        { value: "courrier", label: "Courrier" },
        { value: "rapport", label: "Rapport" },
        { value: "piece_identite", label: "Pièce d'identité" },
        { value: "contrat", label: "Contrat" },
        { value: "attestation", label: "Attestation" },
        { value: "autre_document", label: "Autre document" }
      ]);
      setCategoriesLoaded(true);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/ged/documents?`;
      if (selectedCategory) url += `categorie=${selectedCategory}&`;
      if (searchQuery) url += `search=${searchQuery}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        // L'API retourne {data: [...], pagination: {...}} - Trier du plus récent au plus ancien
        const docs = result.data || result;
        const sortedDocs = Array.isArray(docs) ? docs.sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0)) : docs;
        setDocuments(sortedDocs);
      } else {
        throw new Error('Erreur de chargement');
      }
    } catch {
      setError('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/ged/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch {
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.categorie) {
      setError('Veuillez sélectionner un fichier et une catégorie');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('categorie', uploadForm.categorie);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);

      const response = await fetch(`${API_URL}/api/ged/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (response.ok) {
        setSuccess('Document uploadé avec succès !');
        setShowUploadModal(false);
        setUploadForm({ file: null, categorie: '', description: '', tags: '' });
        loadDocuments();
        loadStats();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc);
    } catch {
      setError('Erreur lors du téléchargement du document');
    }
  };

  const handleView = async (doc) => {
    try {
      await viewDocument(doc);
    } catch {
      setError('Erreur lors de la visualisation du document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/ged/documents/${documentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setSuccess('Document supprimé avec succès');
        loadDocuments();
        loadStats();
      } else {
        throw new Error('Erreur de suppression');
      }
    } catch {
      setError('Erreur lors de la suppression du document');
    }
  };

  const getStatusBadge = (statut) => {
    const badges = {
      'en_attente': { icon: Clock, color: 'bg-yellow-600 text-white', label: 'En attente' },
      'valide': { icon: CheckCircle, color: 'bg-green-700 text-white', label: 'Validé' },
      'rejete': { icon: XCircle, color: 'bg-red-700 text-white', label: 'Rejeté' },
      'archive': { icon: FileText, color: 'bg-gray-600 text-white', label: 'Archivé' }
    };

    const badge = badges[statut] || badges['en_attente'];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header avec stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">{t('ged_common.total_documents', 'Total Documents')}</div>
            <div className="text-2xl font-bold text-blue-600">{stats.total_documents}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">{t('ged_common.invoices', 'Factures')}</div>
            <div className="text-2xl font-bold text-green-600">{stats.par_categorie?.factures || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">{t('ged_common.subscribed_device', 'Dispositif souscrit')}</div>
            <div className="text-2xl font-bold text-purple-600">{stats.par_categorie?.dispositif_souscrit || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">{t('ged_common.space_used', 'Espace Utilisé')}</div>
            <div className="text-2xl font-bold text-orange-600">{stats.taille_totale_mb.toFixed(2)} MB</div>
          </div>
        </div>
      )}

      {/* Messages d'erreur/succès */}
      {error && (
        <div className="bg-red-100 border border-red-300 text-red-900 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-300 text-green-900 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Barre d'actions */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Bouton Upload */}
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload className="w-5 h-5 mr-2" />
            {t('ged_common.upload', 'Uploader un document')}
          </button>

          {/* Filtre par catégorie */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Toutes les catégories</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          {/* Recherche */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher un document..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Liste des documents */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>Aucun document trouvé</p>
            <p className="text-sm mt-2">Commencez par uploader votre premier document</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taille</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {documents.map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{doc.original_filename}</div>
                          {doc.description && (
                            <div className="text-xs text-gray-500">{doc.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {categories.find(c => c.value === doc.categorie)?.label || doc.categorie}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatFileSize(doc.file_size)}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(doc.statut)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(doc.uploaded_at)}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleView(doc)}
                        className="inline-flex items-center px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors"
                        title="Voir"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(doc)}
                        className="inline-flex items-center px-3 py-1 bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {doc.user_id === localStorage.getItem('user_id') && (
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="inline-flex items-center px-3 py-1 bg-red-700 text-white rounded hover:bg-red-800 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Upload */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Uploader un document</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier *
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formats acceptés : PDF, Images, Word, Excel, CSV (max 20 MB)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie * {categories.length > 0 && `(${categories.length} disponibles)`}
                </label>
                <select
                  value={uploadForm.categorie}
                  onChange={(e) => setUploadForm({ ...uploadForm, categorie: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories && categories.length > 0 ? (
                    categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))
                  ) : (
                    <option disabled>Chargement...</option>
                  )}
                </select>
                {categories.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">⚠️ Erreur de chargement des catégories</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brève description du document..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags (séparés par virgules)
                </label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="urgent, 2024, client A..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={uploading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Upload en cours...' : 'Uploader'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
